import React from 'react';

import { t } from 'i18next';

import type { CronJobName } from './CronJobsCRUDForm.types';

import Select from 'components/shared/Select';
import { useCronJobLabel } from './hooks/useCronJobLabel';

import { Alert } from '@mui/material';

interface Props {
  jobNames: CronJobName[];
  selectedJobType?: CronJobName;
  onSelected: (jobName: CronJobName) => void;
  label?: string;
}

/**
 * The job-type dropdown shared by the cron-job tabs.
 *
 * It exists to own one rule in a single place: a job's scope is part of what its label says, and
 * until the API has reported the scopes, "BPM History cleanup" names both the tenant's own cleanup
 * and the one that runs across every tenant in the installation. Offering that choice unlabelled -
 * for the first render, or for good when the request fails - lets an operator schedule an
 * installation-wide job believing it is scoped to their tenant, so the dropdown waits instead and
 * says why when there is nothing to wait for.
 */
export const JobNameSelect: React.FC<Props> = ({
  jobNames,
  selectedJobType,
  onSelected,
  label = t('cronJobs.form.jobName')
}) => {
  const { cronJobLabel, isScopeKnown, isScopeUnavailable } = useCronJobLabel();

  const options = jobNames.map((jobName) => ({
    value: jobName,
    label: cronJobLabel(jobName)
  }));

  return (
    <>
      {isScopeUnavailable && (
        <Alert severity="error">{t('cronJobs.form.scopeUnavailable')}</Alert>
      )}
      <Select
        variant="outlined"
        label={label}
        options={options}
        valueProp={selectedJobType}
        disabled={!isScopeKnown}
        onSelected={onSelected}
      />
    </>
  );
};

export default JobNameSelect;
